import styles from "../../../styles/shared/trade-form/TradeModal.module.css";
import { useDispatch, useSelector } from "react-redux";
import React, { useCallback, useEffect, useRef } from "react";
import {
  getAvailableCoins,
  getIntegratedExchanges,
} from "../../../redux/exchange/actions";
import {
  getTargetPortfolio,
  setTargetExchange,
} from "../../../redux/trade-form/tradeFormSlice";
import { LazyLoadImage } from "react-lazy-load-image-component";
import { Skeleton, Spin } from "antd";
import ErrorMessage from "./messages/ErrorMessage";
import SectionLabel from "./SectionLabel";
import { useLocation } from "react-router-dom";

function ExchangesList({ defaultExchange = "" }) {
  const dispatch = useDispatch();
  const controllerRef = useRef(null);

  // Redux state selectors
  const {
    exchangeState: {
      integratedData,
      integratedDataLoading,
      getIntegratedList,
      integratedDataError,
      selectedPortfolioExchange,
      availableCoinsData,
    },
    tradeForm: { targetExchange, targetPortfolioStatus },
    main: { userData },
  } = useSelector((state) => state);

  const location = useLocation();

  const exchanges = integratedData?.filter((ex) => ex?.status === "Active");

  const abortPreviousRequests = () => {
    if (controllerRef.current) {
      controllerRef.current.abort();
    }
  };

  const fetchIntegratedExchanges = useCallback(() => {
    if (!userData) return;
    dispatch(
      getIntegratedExchanges(
        userData["custom:custom_username"],
        userData["cognito:username"],
      ),
    );
  }, [dispatch, userData]);

  const handleExchangeSelection = useCallback(
    (exchange) => {
      if (!exchange || targetExchange === exchange.id) return;

      if (targetPortfolioStatus === "loading") {
        abortPreviousRequests();
      }

      dispatch(setTargetExchange(exchange.id));

      controllerRef.current = new AbortController();
      const { signal } = controllerRef.current;

      dispatch(
        getTargetPortfolio(
          userData?.["custom:custom_username"],
          userData?.["cognito:username"],
          exchange.id,
          signal,
        ),
      );
    },
    [dispatch, targetExchange, targetPortfolioStatus, userData],
  );

  const selectFirstExchange = useCallback(() => {
    const firstExchangeId = exchanges?.[0]?.id;

    if (!firstExchangeId) return;

    controllerRef.current = new AbortController();
    const { signal } = controllerRef.current;

    dispatch(setTargetExchange(firstExchangeId));
    dispatch(
      getTargetPortfolio(
        userData?.["custom:custom_username"],
        userData?.["cognito:username"],
        firstExchangeId,
        signal,
      ),
    );
  }, [dispatch, exchanges, userData]);

  useEffect(() => {
    if (
      userData?.["cognito:username"] &&
      !integratedDataLoading &&
      !getIntegratedList &&
      !integratedDataError
    ) {
      fetchIntegratedExchanges();
    }
  }, [
    fetchIntegratedExchanges,
    integratedDataLoading,
    getIntegratedList,
    integratedDataError,
  ]);

  useEffect(() => {
    if (!targetExchange) {
      if (exchanges?.length > 1) {
        const defaultExchangeId = location?.pathname?.includes("ai-assistant")
          ? exchanges?.[0]?.id
          : selectedPortfolioExchange || exchanges?.[0]?.id;

        if (defaultExchangeId) {
          handleExchangeSelection({ id: defaultExchangeId });
        }
      } else if (exchanges?.length === 1) {
        selectFirstExchange();
      }
    }
  }, [
    targetExchange,
    selectedPortfolioExchange,
    exchanges,
    handleExchangeSelection,
    selectFirstExchange,
  ]);

  useEffect(() => {
    if (targetExchange && !availableCoinsData?.[targetExchange]) {
      dispatch(getAvailableCoins(targetExchange, true));
    }
  }, [dispatch, targetExchange, availableCoinsData]);

  useEffect(() => {
    if (targetPortfolioStatus === "loading") {
      return () => abortPreviousRequests();
    }
  }, [targetPortfolioStatus]);

  return (
    <>
      <SectionLabel label="Exchange" style={{ marginBottom: "1rem" }} />
      {integratedDataLoading ? (
        <div className={styles.loader}>
          <Skeleton.Node active />
          <div className={styles.spinner}>
            <Spin size="small" />
            <p>Loading integrated exchanges...</p>
          </div>
        </div>
      ) : integratedDataError ? (
        <ErrorMessage>
          Failed to fetch integrated exchange list!{" "}
          <span className={styles.retryBtn} onClick={fetchIntegratedExchanges}>
            Retry
          </span>
        </ErrorMessage>
      ) : integratedData?.length > 0 ? (
        <ul className={styles.exchangeList}>
          {exchanges?.map((ex) => (
            <li
              key={ex?.id}
              className={`${styles.exchange} ${
                targetExchange === ex?.id ? styles.selectedExchange : ""
              }`}
              onClick={() => handleExchangeSelection(ex)}
            >
              <LazyLoadImage
                src={`https://cryptocrispypro.s3.ap-southeast-2.amazonaws.com/crypto_exchanges_logo/${ex?.id}.png`}
                alt={ex?.id}
                wrapperClassName={styles.exchangeIcon}
                effect="blur"
              />
              {ex?.name}
            </li>
          ))}
        </ul>
      ) : (
        <p>You don't integrate with any exchange.</p>
      )}
    </>
  );
}

export default ExchangesList;
